import { offlineLocalSessionsAtom } from '@/atoms/offline-recording';
import { useIsNewSessionListEnabled } from '@/features/flags';
import { releaseRecordingStateAfterSweep } from '@/hooks/desktop/releaseRecordingStateAfterSweep';
import {
  failSessionCreateAttemptAs,
  markSessionCreateNavigationFailed,
  type SessionCreateAttemptOwner,
} from '@/lib/telemetry/sessionCreateTracking';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { sweepCaptureOnSessionEnd } from '@/tauri/online-chunk-upload/session-end-sweep';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { buildOfflineLocalSessionStub } from '@/utils/offline-recording/offlineSessionStub';
import {
  buildOfflineCreateArgs,
  resolveLastUsedRecorderSettings,
} from '@/utils/offline-recording/recorderSettings';
import { toast, ToastActionButton } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import {
  buildSessionPath,
  GET_SESSION_BY_ID,
  isLegacySessionDetailRoute,
} from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { createElement, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
// The design-system toast door exports `toast` but no `dismiss`, and a sticky
// toast with an action that recovers needs one. Same import as the established
// desktop precedent in `useChronicleNotifications.tsx`. Exposing `dismiss` from
// the door would be the durable fix and is a design-system decision.
import { toast as sonnerToast } from 'sonner';
import { STOP_RECORDING_MUTATION_KEY } from './recordingMutationKeys';

export const CREATE_OFFLINE_SESSION_MUTATION_KEY = 'CreateOfflineSession';

/**
 * Failure copy for the offline-capture entry point.
 *
 * PLATFORM-AWARE BY CONSTRUCTION. This path only ever runs inside Tauri — the
 * `mutationFn` throws otherwise — so there is no browser to send anyone to. No
 * "refresh the page", no "check your browser", no address bar: on the desktop
 * app none of those exist, and the clinician is left with instructions they
 * cannot follow. The recovery that IS available to them is to press the control
 * again, and failing that to restart the app.
 *
 * The two failures are kept apart because they leave the clinician in different
 * places. A refused create means no consult exists. A refused navigation means
 * one DOES, and telling that clinician nothing was saved would send them off to
 * re-create a consult they already have.
 *
 * Neither string interpolates anything. No session id, no patient link, no
 * backend detail — nothing that could carry PHI reaches a toast on this path.
 */
const messages = defineMessages({
  createFailedTitle: {
    defaultMessage: "Couldn't start an offline session",
    id: '5m3pwgBUkQ',
    description:
      'Error toast title. The offline recording could not be created — nothing was saved and no session exists. Distinct from the "couldn\'t open" pair below, where one WAS created.',
  },
  createFailedDescription: {
    defaultMessage:
      'Nothing was saved. Try again, or restart Heidi if this keeps happening.',
    id: '7KRdIfIOpK',
    description:
      'Error toast body for a failed offline-session create. Reassures the clinician nothing was half-saved, and gives the only two recoveries available in the desktop app.',
  },
  navigationFailedTitle: {
    defaultMessage: "Couldn't open the offline session",
    id: 'mNihFszbf0',
    description:
      'Error toast title. The offline session WAS created successfully but the app could not navigate to it. Must not imply the session was lost.',
  },
  navigationFailedDescription: {
    defaultMessage: 'It was created and is in your session list.',
    id: 'G29l+ifE9d',
    description:
      'Error toast body for a failed navigation to an offline session that was created. "It" is the session.',
  },
  // DELIBERATELY has no `description`, unlike its four siblings above.
  //
  // The id is a content hash over defaultMessage + description, so adding one
  // forks this descriptor onto a NEW id. This string currently shares
  // `lTKuAGoTLG` with `MultiplayerHandoffBlock.tsx` — identical copy, identical
  // meaning — and that shared id is translated in 17 locale files. A
  // description here would trade a fully-translated button for an untranslated
  // one until Crowdin round-trips, which is a worse outcome than the ambiguity
  // it would resolve. The other four carry descriptions precisely because they
  // are new strings with nothing to lose.
  navigationFailedAction: {
    defaultMessage: 'Open session',
    id: 'lTKuAGoTLG',
  },
});

/**
 * Creates a local offline session via Tauri and navigates to the session page.
 * Routed in (US2) when the offline-recording flag is on and the residency-pinned
 * backend is unreachable (`offlineBackendUnreachableAtom`).
 *
 * The local UUID has no server record until sync (US2 / APP-6182), so the
 * background session-by-id refetch will 404. TQ v5 flips `isError` true even
 * with retained cached data; SessionPageContent gates the error page on `isError
 * && !session`, so the recorder UI stays mounted while the stub is present.
 * The stub is replaced with real server data once sync completes.
 */
// `attempt` is set only by the caller that opened the session_create attempt,
// so an untracked create never closes someone else's.
type OfflineCreateVariables = { attempt?: SessionCreateAttemptOwner } | undefined;

export function useMutateCreateOfflineSession() {
  const queryClient = useQueryClient();
  const jotaiStore = useStore();
  const router = useRouter();
  const intl = useIntl();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const setOfflineLocalSessions = useSetAtom(offlineLocalSessionsAtom);
  const lastCreateArgsRef = useRef<ReturnType<typeof buildOfflineCreateArgs> | null>(
    null
  );

  return useMutation<string, unknown, OfflineCreateVariables>({
    mutationKey: [CREATE_OFFLINE_SESSION_MUTATION_KEY],
    // `mutationFn` is a local Tauri call, not a network request — it must run
    // *because* we're offline. React Query's default `networkMode: 'online'`
    // would pause the mutation while offline (the exact bug this fixes), so we
    // force `'always'`. No `retry` is configured (mutations default to none),
    // so there is no retry-driven duplicate-creation path; sync (US2/APP-6182)
    // owns reconciling the local stub with the server record.
    networkMode: 'always',
    mutationFn: async (): Promise<string> => {
      if (!isTauri()) {
        throw new Error('offline_recording_create is only available in Tauri');
      }
      // FR-006a (APP-6502): pre-fill the recording's per-session settings from the
      // user's last-used values (cached from the preceding online session) so the
      // synced note matches what they would have got online. `patientLink` is
      // wired separately in APP-6501.
      const args = buildOfflineCreateArgs();
      lastCreateArgsRef.current = args;
      // This path is New Session — it never names a `serverSessionId`, so the
      // create always mints and the `reused` / `hasCapturedAudio` half of the
      // result is constant here. The id is all that is onward-relevant; the
      // capture-intent decision belongs to `useRecordingButton`, which is the
      // caller that can hit the reuse branch.
      const { localSessionId } = await offlineRecordingBridge.create(args);

      // FR-038: the third of `useNewSession`'s creators, and the one most likely
      // to be reached WHILE an online recording is live — `routeOffline()` is
      // checked BEFORE `routeOnline()` (`useNewSession.ts`), so a clinician
      // recording online whose connectivity degrades takes this route when they
      // hit New Session. The ONLINE chunk upload it leaves behind is exactly
      // what the sweep stops, and it can still do so with no network: the bridge
      // is a local `invoke` (`src/tauri/online-chunk-upload/session.ts`), not an
      // HTTP call.
      //
      // AFTER the local create, not before it, for the reason the two sibling
      // creators carry: capture may only be stopped once the session it belongs
      // to has ACTUALLY ENDED. `offline_recording_create` can fail — a full or
      // unwritable cache directory, a keychain read — and `onError` below only
      // logs, so a failed create leaves the clinician on the page they were
      // already on with no new session and nowhere to have gone. Sweeping first
      // would have ended that consultation's capture silently.
      //
      // UNGATED, unlike the two sibling creators, and deliberately so rather
      // than by omission: this hook takes no props and its `onSuccess` pushes
      // the route unconditionally (`router.push(sessionPath)` below), so every
      // consumer navigates and there is no `navigate: false` case to exclude.
      // If a `navigate` prop is ever added here, this sweep needs the same gate
      // the siblings have.
      //
      // `anyActiveUpload`, matching the sibling creators. When this route is
      // reached via the own-user create's network-error fallback, that create
      // FAILED and therefore did NOT sweep — this is the only sweep on that
      // path, not a second one. Awaited so capture is terminated before
      // `onSuccess` pushes the redirect, and safe to ignore: the helper never
      // throws and never blocks the create on failure.

      // Stand aside while the user's own stop drains: it holds
      // `RECORDING_STOP_COORDINATOR`, so this sweep's stop could only wait out
      // the deadline. Constraint in full: `useMutateCreateNewSession.ts`.
      const sweepResult = await sweepCaptureOnSessionEnd({
        target: { kind: 'anyActiveUpload' },
        callSite: 'createOfflineSession',
        stopInFlight:
          queryClient.isMutating({ mutationKey: [STOP_RECORDING_MUTATION_KEY] }) > 0,
      });

      // Stopping native capture is only half the job, and the half that was
      // missing is the one the NEXT recording depends on. Whatever the sweep
      // terminated, the frontend still claimed a recording was in progress —
      // the shared timer, the live-session atom, the transport stamp, the
      // cached `recording_status`. A stale `timerModel.isRunning` does not
      // merely mislead the UI: `runRecorderSafetyStop` returns early on it
      // TWICE, so it also disables the last-resort force-stop for every
      // genuine runaway afterwards. Release the claim, and only on a result
      // that entitles it — the helper decides which do.
      //
      // `sessionId: null` because this call site targets `anyActiveUpload`: it
      // has no session of its own to release, so whichever session the
      // frontend still claims is the one released.
      releaseRecordingStateAfterSweep({
        result: sweepResult,
        sessionId: null,
        jotaiStore,
        queryClient,
      });

      return localSessionId;
    },
    onSuccess: (localSessionId: string, variables) => {
      const args = lastCreateArgsRef.current ?? {};
      const createdAt = new Date().toISOString();
      // Template and note style are read from the defaults rather than off
      // `args`, because DSK-873 stopped `buildOfflineCreateArgs` from sending
      // them: persisting them would snapshot the account defaults at create and
      // then override the fresher values the sync engine resolves. The pickers
      // must still SHOW the defaults before recording starts, and displaying
      // them is not the same as freezing them — so they go on the stub only.
      const defaults = resolveLastUsedRecorderSettings();
      // Shared with the cold-boot rehydration path (useOfflineRecordingEvents)
      // so the two offline-local stubs can't drift; see buildOfflineLocalSessionStub
      // for why `created_at` is deliberately omitted (APP-7792 double-`Z` guard).
      const stub = buildOfflineLocalSessionStub(localSessionId, {
        transcribeMode: args.dictationType,
        language: args.language,
        outputLanguage: args.outputLanguage,
        templateId: defaults?.templateId,
        noteStyle: defaults?.voiceStyle,
      });

      queryClient.setQueryData([GET_SESSION_BY_ID, { sessionId: localSessionId }], stub);
      setOfflineLocalSessions((prev) => {
        const nextLocalSession = {
          localSessionId,
          serverSessionId: args.serverSessionId ?? null,
          createdAt,
          updatedAt: createdAt,
        };
        return prev.some((session) => session.localSessionId === localSessionId)
          ? prev.map((session) =>
              session.localSessionId === localSessionId ? nextLocalSession : session
            )
          : [nextLocalSession, ...prev];
      });
      // A user already sitting on the legacy page stays on it, so the flag alone
      // does not decide the route here.
      const sessionPath = buildSessionPath(
        localSessionId,
        isNewSessionListEnabled && !isLegacySessionDetailRoute(router.pathname)
      );
      // Named and self-referencing so the toast's action RE-NAVIGATES rather
      // than telling the clinician to try again.
      //
      // THIS IS A DUPLICATE-CONSULT GUARD, not a nicety. The only control a
      // clinician can reach from here is the one that created this session, so
      // "try again" means "press create again" — which runs the mutation a
      // second time and mints a second local session id for one encounter.
      // `offlineLocalSessionsAtom` dedupes on that id, so the two never merge
      // and the orphan syncs as a second consult. Recovering re-pushes the path
      // that ALREADY exists: no mutation, no bridge call, no second row.
      const navigateToCreatedSession = (): void => {
        router.push(sessionPath).catch((error: unknown) => {
          // sanitizeError, not the raw error: a router or third-party rejection
          // can embed a URL carrying credential query parameters, and the
          // Datadog transport applies no redaction of its own. `errorName` is a
          // constructor name — bounded, so it carries no PHI of its own — and
          // without it a non-axios rejection sanitises to `{}` and two
          // different failures are indistinguishable in Datadog.
          logger.error('[useMutateCreateOfflineSession] navigation failed', {
            localSessionId,
            errorName: error instanceof Error ? error.name : typeof error,
            error: sanitizeError(error),
          });
          // Left open: the toast's retry can still mount the page.
          if (variables?.attempt) markSessionCreateNavigationFailed();
          const toastId = toast({
            tone: 'destructive',
            title: intl.formatMessage(messages.navigationFailedTitle),
            description: intl.formatMessage(messages.navigationFailedDescription),
            // The clinician has to be able to act on this one, so it must not
            // time out from under them.
            duration: Infinity,
            // `createElement` rather than JSX so this stays a `.ts` file: the
            // rename to `.tsx` reads as a new file to the session-data and
            // cache-key ratchets, and churning two baselines to buy syntax
            // sugar is not a trade worth making.
            action: createElement(
              ToastActionButton,
              {
                tone: 'destructive',
                // Dismiss BEFORE re-pushing, not after. `ToastActionButton` is
                // a styled Button and wires no dismiss of its own — only the
                // close (X) does — so a sticky toast whose retry SUCCEEDS would
                // otherwise sit on the canvas the clinician just landed on,
                // telling them the consult did not open. Dismissing first also
                // keeps a second failure to exactly one toast rather than
                // stacking against the Toaster's three-toast ceiling.
                onClick: () => {
                  sonnerToast.dismiss(toastId);
                  navigateToCreatedSession();
                },
              },
              intl.formatMessage(messages.navigationFailedAction)
            ),
          });
        });
      };
      navigateToCreatedSession();
    },
    onError: (error: unknown, variables) => {
      // See the navigation catch above: the raw error never reaches telemetry.
      //
      // `errorName` is load-bearing here rather than symmetry. What rejects on
      // this path is a Tauri `invoke` carrying a serialized Rust `Err`, which is
      // not an AxiosError — so `sanitizeError` alone projects it to `{}` and the
      // failure this hook exists to report reaches Datadog with no class, no
      // command and no way to tell two different Rust refusals apart.
      logger.error('[useMutateCreateOfflineSession] mutationFn failed', {
        errorName: error instanceof Error ? error.name : typeof error,
        error: sanitizeError(error),
      });
      if (variables?.attempt)
        failSessionCreateAttemptAs('SessionCreateOfflineStoreFailed');
      // The online create path (`useMutateCreateNewSession`) SUPPRESSES its own
      // network-error toast when it is about to hand over to offline capture,
      // on the grounds that the create has not really failed yet. That hand-off
      // ends here, so this is the only place left that can tell the clinician
      // the capture never started — without it the suppression makes the whole
      // fallback silent.
      toast({
        tone: 'destructive',
        title: intl.formatMessage(messages.createFailedTitle),
        description: intl.formatMessage(messages.createFailedDescription),
      });
    },
  });
}
